import { ScrollArea } from '@/components/ui/scroll-area';
import { Train, ExternalLink, RefreshCw, Clock } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrainInfoSection = () => {
  const [departures, setDepartures] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchTrainData = async () => {
    try {
      const [depRes, arrRes] = await Promise.allSettled([
        axios.get(`${API}/trains/departures`),
        axios.get(`${API}/trains/arrivals`)
      ]);
      
      if (depRes.status === 'fulfilled') setDepartures(depRes.value.data);
      if (arrRes.status === 'fulfilled') setArrivals(arrRes.value.data);
      
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching train data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainData();
    const interval = setInterval(fetchTrainData, 2 * 60 * 1000); // Update every 2 minutes
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="content-card content-card-large" data-testid="train-section">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>
          <Train size={20} className="inline" /> King's Lynn Station
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button 
            onClick={fetchTrainData} 
            variant="ghost" 
            size="sm"
            style={{ padding: '0.4rem 0.8rem' }}
            data-testid="refresh-train-button"
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>
      <ScrollArea className="card-content">
        <div className="transport-container">
          {/* Live Departures Display */}
          <div className="transport-subsection">
            <div className="transport-subsection-header">
              <Train size={18} />
              <h3>Live Departures</h3>
              <a 
                href="https://www.nationalrail.co.uk/live-trains/departures/kings-lynn/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="header-external-link"
                title="View full departures board"
              >
                <ExternalLink size={16} />
              </a>
            </div>
            
            {loading ? (
              <p className="loading-text">Loading departure times...</p>
            ) : (
              <div className="train-board">
                {departures.length > 0 ? (
                  <>
                    {departures.map((train, index) => (
                      <div key={`dep-${train.time}-${train.destination}-${index}`} className="train-board-row" data-testid={`train-dep-${index}`}>
                        <div className="train-time-col">
                          <span className="train-time-display">{train.time}</span>
                        </div>
                        <div className="train-dest-col">
                          <span className="train-destination">{train.destination}</span>
                          <span className="train-operator-small">{train.operator}</span>
                        </div>
                        <div className="train-platform-col">
                          <span className="platform-label">Plat</span>
                          <span className="platform-number">{train.platform}</span>
                        </div>
                        <div className="train-status-col">
                          <span className={`status-badge ${train.status.toLowerCase().includes('on time') ? 'status-on-time' : train.status.toLowerCase().includes('delay') || train.status.toLowerCase().includes('late') ? 'status-delayed' : 'status-info'}`}>
                            {train.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="train-info-message">
                    <p>No departure information available</p>
                    <p className="info-subtext">Click the link above to view live departures on National Rail</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Live Arrivals Display */}
          <div className="transport-subsection">
            <div className="transport-subsection-header">
              <Train size={18} />
              <h3>Live Arrivals</h3>
              <a 
                href="https://www.nationalrail.co.uk/live-trains/arrivals/kings-lynn/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="header-external-link"
                title="View full arrivals board"
              >
                <ExternalLink size={16} />
              </a>
            </div>
            
            {loading ? (
              <p className="loading-text">Loading arrival times...</p>
            ) : (
              <div className="train-board">
                {arrivals.length > 0 ? (
                  <>
                    {arrivals.map((train, index) => (
                      <div key={`arr-${train.time}-${train.destination}-${index}`} className="train-board-row" data-testid={`train-arr-${index}`}>
                        <div className="train-time-col">
                          <span className="train-time-display">{train.time}</span>
                        </div>
                        <div className="train-dest-col">
                          <span className="train-destination">{train.destination}</span>
                          <span className="train-operator-small">{train.operator}</span>
                        </div>
                        <div className="train-platform-col">
                          <span className="platform-label">Plat</span>
                          <span className="platform-number">{train.platform}</span>
                        </div>
                        <div className="train-status-col">
                          <span className={`status-badge ${train.status.toLowerCase().includes('on time') ? 'status-on-time' : train.status.toLowerCase().includes('delay') || train.status.toLowerCase().includes('late') ? 'status-delayed' : 'status-info'}`}>
                            {train.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="train-info-message">
                    <p>No arrival information available</p>
                    <p className="info-subtext">Click the link above to view live arrivals on National Rail</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="transport-quick-links">
            <a 
              href="https://www.nationalrail.co.uk/stations/kings-lynn/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="quick-link-btn"
            >
              Station Information
            </a>
            <a 
              href="https://www.nationalrail.co.uk/travel/plan-your-journey/"
              target="_blank" 
              rel="noopener noreferrer"
              className="quick-link-btn"
            >
              Journey Planner
            </a>
          </div>

          <div className="transport-update-time">
            <Clock size={14} />
            <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default TrainInfoSection;